import type { IUserInfo, IWaterMarkProps, ResourceStatus } from '@umu/umu-types';
import type { RefObject, CSSProperties, ReactNode } from 'react';

/**
 * 全屏状态
 */
export type SCREEN_STATE = 'no-full' | 'full';

/**
 * 渲染类型
 */
export enum RENDER_TYPE {
    CANVAS = 1,
    SVG = 2,
    IMAGE = 3,
}

/**
 * 白名单类型
 */
export enum RENDER_HIT_TYPE {
    NONE = 0,
    ENTERPRISE_ID,
    ELEMENT_ID,
    PDF_URL,
}

/**
 * pdf的图片信息
 */
export interface IPdfImages {
    path: string;
    list: string[];
}

export type Dest = [{ num: number; gen: number }];

export type PdfOutline = {
    title: string;
    items: Outlines;
    pageNum: number;
    dest?: Dest;
    count?: number;
};

export type Outlines = PdfOutline[];

export type PageFlipMode = 'vertical' | 'horizontal';

/**
 * 搜索结果信息
 */
export interface IStaticInfo {
    page: number; // 页码
    index?: number; // 在该页的第几个匹配项 (0-based)
    keyword?: string; // 匹配的关键词
    rows?: number;
    col?: number;
}

/**
 * PdfReader Props
 */
export interface IPdfReaderProps {
    documentTitle?: string;
    userInfo?: IUserInfo;
    allowSearch?: boolean; // 是否允许搜索
    isShowWatermark?: boolean; // 是否开启水印
    waterMarkOptions?: IWaterMarkProps;
    resourceUrl: string; // 文档 recourse url
    pdfImages?: IPdfImages; // pdf 图片列表
    initPageNum?: any; // 初始页码
    screenState?: SCREEN_STATE; // 全屏状态
    screenCallback?: (isFullscreen: boolean) => void; // 全屏按钮回调
    transStatus?: ResourceStatus; // 转码状态
    pageChangeCallback?: (pageNum: number, allPageNum: number) => void; // 页码切换回调
    elementId?: string;
    computedReaderHeight?: (params?: { [key: string]: any }) => string;
    onFailedCallback?: () => void; //资源请求失败 | 删除回调函数
    cMapUrl?: string; // PDF.js CMap URL（用于字体映射）
    pdfJsWorkerUrl?: string; // PDF.js Worker URL

    // New props merged from businessCommon
    style?: CSSProperties;
    className?: string;
    offsetTop?: number; // 顶部偏移量
    containerHeight?: number; // 限制容器高度

    // New props for decoupling
    onError?: (error: any) => void;
    loadingComponent?: ReactNode;
}

// Alias for backward compatibility if needed within internal components
export type IProps = IPdfReaderProps;

export interface IPdfReaderState {
    networkError?: boolean;
    renderType: RENDER_TYPE;
    renderPixelRadio?: number;
    renderTypeReady?: boolean; // 渲染类型配置是否完成
    reRender?: boolean;
    pageNum: {
        value: number;
    };
    displayPageNum?: number; // 当前展示的页码
    totalPages: number; // 总页数
    pageChangeCallback?: (targetPage: number, allPageNum: number) => void; // 页码变化回调
    screenState: SCREEN_STATE;
    scale: number;
    renderScale?: number; // canvas 渲染 scale（仅 Canvas 模式下可能与 scale 不同）
    initScale?: number;
    lastScale?: number;
    isError?: boolean;
    isRendered?: boolean;
    isShowBar: boolean;
    loading: boolean;
    pdf?: any; // 旧代码中的属性名
    pdfDoc?: any; // 新代码中使用的属性名
    pageFlipMode: PageFlipMode;
    pdfOutlines?: Outlines;
    outlines?: Outlines; // 新代码中使用的属性名
    showOutlines: boolean;
    error?: any; // 错误信息
    pdfImages?: IPdfImages; // PDF 图片列表（用于 Image 渲染模式）
    enableCjkFix?: boolean; // iOS CJK PUA 字体修复开关（由 API 返回的 iosCjk + iosCjkVersion 计算得出）

    // Refs
    pdfContentRef?: RefObject<HTMLDivElement>;
    canvasRef?: RefObject<HTMLCanvasElement>;
    svgContainerRef?: RefObject<HTMLDivElement>;
    docContainerRef?: RefObject<HTMLDivElement>;
    // props
    docReaderProps?: IPdfReaderProps;
}

/**
 * PdfReader 组件通过 ref 暴露的方法接口
 */
export interface IPdfReaderRef {
    // 工具栏相关方法
    zoomIn: () => void; // 放大
    zoomOut: () => void; // 缩小
    zoomReset: () => void; // 重置缩放
    jumpTo: (page: number) => void; // 跳转到指定页
    nextPage: () => void; // 下一页
    prevPage: () => void; // 上一页
    toggleFullScreen: () => void; // 切换全屏
    toggleOutline: () => void; // 切换大纲显示
    closeOutline: () => void; // 关闭大纲
    changeFlipMode: (mode: PageFlipMode) => void; // 切换翻页模式
    onSearch: (value: string) => Promise<IStaticInfo[]>; // 搜索
    onSearchClose: () => void; // 关闭搜索
    onJumpToElement: (element: IStaticInfo) => Promise<boolean>; // 跳转到搜索结果元素
    showToolbar: () => void; // 显示工具栏
    hideToolbar: () => void; // 隐藏工具栏
    toggleToolbar: () => void; // 切换工具栏显示状态
}

/**
 * PDF 搜索相关的参数接口
 */

/**
 * 处理 chunk 页面的参数
 */
export interface IProcessChunkPagesParams {
    startPage: number;
    endPage: number;
    searchId: number;
    term: string;
    value: string;
    currentPage?: number;
}

/**
 * 处理单个块的参数
 */
export interface IProcessChunkParams extends IProcessChunkPagesParams { }

/**
 * 处理批次的参数
 */
export interface IProcessBatchParams {
    batch: Array<{ startPage: number; endPage: number }>;
    searchId: number;
    term: string;
    value: string;
    currentPage?: number;
}

/**
 * 并发控制处理多个块的参数
 */
export interface IProcessChunksWithConcurrencyParams {
    chunks: Array<{ startPage: number; endPage: number }>;
    searchId: number;
    term: string;
    value: string;
    currentPage?: number;
    concurrency?: number;
}
